<template>
  <div class="mb-2 chart-div" :style="props.style" v-if="speedAxisDataset.data.length > 0">
    <Line
      :plugins="mergedPlugins"
      :options="chartOptions"
      :data="chartData"
      :chart-id="chartId"
      dataset-id-key="label"
      ref="chartInstance"
    />
    <div id="legend-container" />
  </div>
</template>

<script setup lang="ts" >

import i18n from '@/lang/i18n';
import { PropType, ref, Ref, watch, computed, onMounted } from 'vue'
import { Line } from 'vue-chartjs'
import {
  CategoryScale,
  Chart as ChartJS,
  Chart,
  ChartDataset,
  ChartEvent,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  Plugin,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import { AnyObject } from 'chart.js/types/basic'
import TimelineHelper from '@/services/helpers/TimelineHelper'
import { timeLinePosition, timeLineProducer } from '@/types/timeLinePosition'
import { Point } from 'chart.js/types/geometric'

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  TimeScale,
  PointElement,
  CategoryScale
)

import Api from "@/services/api/ApiServiceFabrick";
const minTimeRange = 250;

const chartTimeLinePositionInPercentage = ref<number>(0)
const isItChartEvent = ref<boolean>(false)
const drawTimeLineLocalContext = ref<boolean>(true)

const props = withDefaults(
  defineProps<{
    id: number
    chartId: string
    globalTimeLinePosition: object
    arGyroscopeHistory: object[]
    arSpeedHistory: object[]
    alertType: string
    plugins: any[]
    drawTimeLine: boolean
    style: string
  }>(),
  {
    chartId: 'line-chart',
    arGyroscopeHistory: () => [],
    arSpeedHistory: () => [],
    plugins: () => [],
    drawTimeLine: true,
  }
)

const emits = defineEmits(['isReadyToPlayChart'])

const data = ref(null)
const dateTimes = ref([])
const speedData:Ref<Point[]> = ref([])

onMounted( async () => {
  data.value = await Api().getCvrUploadMeasuringData(props.id);
  const _data = getSpeeds()
  emits('isReadyToPlayChart', !!_data[0])
})

function convertTimeFromSpeedTime(origin) {
  const times = origin.split('.')
  const converted = times[0].padEnd(14, '0')
  const y = times[0].substring(0, 4)
  const m = times[0].substring(4, 6)
  const d = times[0].substring(6, 8)
  const h = times[0].substring(8, 10)
  const min = times[0].substring(10, 12)
  const s = times[0].substring(12, 14)
  const millisecconds = times[1] ?? '0'
  return (new Date(`${y}-${m}-${d} ${h}:${min}:${s}.${millisecconds}`));
}

function getSpeeds() {
  if (!data.value?.speeds) return [];

  const _speeds = originSpeeds.value;
  if (_speeds.length == 0) return [];

  const firstSpeed = _speeds[0];
  const firstTime = firstSpeed.unixTime;
  const lastSpeed = _speeds[_speeds.length - 1];
  const lastTime = lastSpeed.unixTime;
  let totalRange = lastTime - firstTime;

  const retSpeeds = [];
  let speedIndex = 0;
  const _speedData = [];
  for (let i = 0; i * minTimeRange <= totalRange; i++) {
    const currentSpeed = _speeds[speedIndex];
    if (!currentSpeed) break;
    if (currentSpeed.unixTime < firstTime + i * minTimeRange) {
      speedIndex++;
      continue;
    }

    const nextTime = firstTime + (i + 1) * minTimeRange;
    if (currentSpeed.unixTime < nextTime) {
      retSpeeds.push(currentSpeed);
      _speedData.push({
        x: currentSpeed.unixTime,
        y: currentSpeed.speed
      });
      dateTimes.value.push(currentSpeed.convertedTime)
      speedIndex++;
      continue;
    }
    retSpeeds.push({
      convertedTime: new Date(nextTime),
      unixTime: nextTime,
      speed: null
    });
    dateTimes.value.push(new Date(nextTime))
    _speedData.push({
        x: nextTime,
        y: null
    })
  }
  speedData.value = _speedData;
  return retSpeeds;
}

const originSpeeds = computed(() => {
  const _speeds = data.value?.speeds ?? [];
  _speeds.forEach((s) => {
    s.convertedTime = convertTimeFromSpeedTime(s.time)
    s.unixTime = convertTimeFromSpeedTime(s.time).getTime()
  })
  return _speeds.sort((a, b) => {
    return (a.convertedTime < b.convertedTime) ? -1 : 1;
  });
})

function drawTimelineOnChart(
  chart: Chart,
  positionInPercentage: number | undefined
): void {
  if (!positionInPercentage) {
    return
  }
  const chartAreaPositionX = TimelineHelper.convertPercentToNumber(
    positionInPercentage,
    chart.chartArea.width
  )
  let chartPositionX = chartAreaPositionX + chart.chartArea.left

  let ctx = chart.ctx
  ctx.save()
  ctx.beginPath()
  ctx.moveTo(chartPositionX, chart.chartArea.top)
  ctx.lineTo(chartPositionX, chart.chartArea.bottom)
  ctx.lineWidth = 1
  ctx.strokeStyle = '#E0E0E0'
  ctx.stroke()
  ctx.restore()
  ctx.save()

  ctx.setLineDash([])
}

function wasClickInsideChartArea(event: ChartEvent, chart: Chart): boolean {
  if (!event.x || !event.y) {
    return false
  }

  return (
    event.x >= chart.chartArea.left &&
    event.x <= chart.chartArea.right &&
    event.y >= chart.chartArea.top &&
    event.y <= chart.chartArea.bottom
  )
}

interface Props {
  globalTimeLinePosition?: timeLinePosition
  drawTimeLine?: boolean
}

const chartInstance = ref(null)
const propsForTS = props as Props

watch(
  () => propsForTS.globalTimeLinePosition,
  (timeLinePosition) => {
    if (chartInstance.value && timeLinePosition) {
      chartTimeLinePositionInPercentage.value = Number(
        timeLinePosition.positionInPercentage
      )
      const chart = (chartInstance.value as any).chart as Chart
      chart.update()
    }
  }
)

watch(
  () => propsForTS.drawTimeLine,
  (value) => {
    drawTimeLineLocalContext.value = value || false
  }
)

function getDefaultPlugins() {
  return [
    htmlLegendPlugin,
    {
      id: 'myEventCatcher',
      afterDraw(chart: Chart) {
        if (drawTimeLineLocalContext.value) {
          drawTimelineOnChart(
            chart,
            chartTimeLinePositionInPercentage.value
          )
        }
      },
      afterEvent(chart: Chart, args: AnyObject) {
        const event = args.event as ChartEvent
        if (event.type === 'click' && event.x && event.y) {
          if (wasClickInsideChartArea(args.event as ChartEvent, chart)) {
            const chartClickPositionX = event.x
            const chartAreaClickPositionX =
              chartClickPositionX - chart.chartArea.left
            chartTimeLinePositionInPercentage.value =
              TimelineHelper.convertNumberToPercent(
                chartAreaClickPositionX,
                chart.chartArea.width
              )
            isItChartEvent.value = true
          }
        }
      },
    },
    Filler,
  ]
}

const mergedPlugins = computed(() => {
  const defaultPlugins = getDefaultPlugins()
  return props.plugins.concat(defaultPlugins)
})

const chartData = computed(():object => {
  let datasets: ChartDataset[] = []
  datasets.push(speedAxisDataset.value)

  return {
    labels: dateTimes.value,
    datasets: datasets,
  }
})

const speedAxisDataset = computed((): ChartDataset => {
  return {
    yAxisID: 'y',
    label: i18n.global.t('pages.cvr_uploads.previewModal.speed'),
    borderColor: 'rgba(134, 195, 225, 1)',
    pointBackgroundColor: 'rgba(0, 0, 0, 0)',
    // pointBackgroundColor: 'rgba(134, 195, 225, 1)',
    data: speedData.value,
    borderWidth: 1,
    pointBorderWidth: 0,
    pointRadius: 2,
    fill: true,
  }
})

const getOrCreateLegendList = (chart, id) => {
  const legendContainer = document.getElementById(id);
  let listContainer = legendContainer.querySelector('ul');

  if (!listContainer) {
    listContainer = document.createElement('ul');
    listContainer.style.display = 'flex';
    listContainer.style.flexDirection = 'row';
    listContainer.style.margin = 0;
    listContainer.style.padding = 0;

    legendContainer.appendChild(listContainer);
  }

  return listContainer;
}

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart, args, options) {
    const ul = getOrCreateLegendList(chart, options.containerID);

    // Remove old legend items
    while (ul.firstChild) {
      ul.firstChild.remove();
    }

    // Reuse the built-in legendItems generator
    const items = chart.options.plugins.legend.labels.generateLabels(chart);

    items.forEach(item => {
      const li = document.createElement('li');
      li.style.alignItems = 'center';
      li.style.cursor = 'pointer';
      li.style.display = 'flex';
      li.style.flexDirection = 'row';
      li.style.marginLeft = '10px';

      li.onclick = () => {
        const {type} = chart.config;
        if (type === 'pie' || type === 'doughnut') {
          // Pie and doughnut charts only have a single dataset and visibility is per item
          chart.toggleDataVisibility(item.index);
        } else {
          chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
        }
        chart.update();
      };

      // Color box
      const boxSpan = document.createElement('span');
      boxSpan.style.background = item.fillStyle;
      boxSpan.style.borderColor = item.strokeStyle;
      boxSpan.style.backgroundColor = item.strokeStyle;
      boxSpan.style.borderWidth = item.lineWidth + 'px';
      boxSpan.style.display = 'inline-block';
      boxSpan.style.flexShrink = 0;
      boxSpan.style.height = '10px';
      boxSpan.style.marginRight = '5px';
      boxSpan.style.width = '10px';
      boxSpan.style.borderRadius = '3px';

      // Text
      const textContainer = document.createElement('p');
      textContainer.style.color = '#E0E0E0';
      textContainer.style.margin = 0;
      textContainer.style.padding = 0;
      textContainer.style.textDecoration = item.hidden ? 'line-through' : '';
      textContainer.style.fontSize = 'smaller'

      const text = document.createTextNode(item.text);
      textContainer.appendChild(text);

      li.appendChild(boxSpan);
      li.appendChild(textContainer);
      ul.appendChild(li);
    });
  }
}

const chartOptions = computed(() => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: chartOptionsScales.value,
    plugins: {
      htmlLegend: {
        containerID: 'legend-container',
      },
      legend: {
        display: false,
      },
    },
  }
})

const chartOptionsScales = computed(() => {
  const scales: any = {
    x: timeScaleSetting.value,
    y: speedScaleSetting.value
  }

  return scales
})

const timeScaleSetting = computed(() => {
  return {
    type: 'time',
    display: true,
    position: 'bottom',
    ticks: {
      color: '#E0E0E0',
      stepSize: 10,
    },
    time: {
      unit: 'second',
      displayFormats: {
        second: 'HH:mm:ss',
      },
      tooltipFormat: 'HH:mm:ss',
    },
  }
})

const speedScaleSetting = computed(() => {
  return {
    id: i18n.global.t('pages.cvr_uploads.previewModal.speed'),
    type: 'linear',
    display: true,
    position: 'left',
    ticks: {
      color: '#E0E0E0',
      stepSize: 10,
    },
    min: 0,
    grid: {
      drawOnChartArea: true,
      color: "rgba(100, 100, 100, 0.2)"
    },
  }
})
</script>

<style scoped>
.note {
  font-size: 0.6rem;
  line-height: 1rem;
}
.chart-div {
  height: fit-content;
  width: 100%;
}
</style>
